// Public per-shift recap page at /recap/{shiftId}. Source: publicShiftRecaps/{shiftId}.
// Built by buildShiftRecap Cloud Function on shift close. PII-safe by design —
// recipient names omitted, customer data omitted. Meant to be delivered to
// clients post-event so they can share with attendees.
//
// "By the numbers" redesign (2026-07-06): count-up stat band (hours of live art,
// pancakes, artists, meditations, avg draw), pancake trading-cards with
// superlative ribbons (most intricate / first / last), and a lightbox that
// surfaces each pancake's stats + meditation. All values come from fields
// already on the recap doc — no schema change.

import { useEffect, useState, useRef, useMemo } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import SiteNav from "./components/SiteNav";

// Pin America/Chicago — booth's home tz. Without it, non-Chicago viewers (and
// UTC-midnight calendar-day markers) can render the wrong day.
function fmtDate(ms) {
  if (!ms) return null;
  return new Date(ms).toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
    timeZone: "America/Chicago",
  });
}
function fmtTime(ms) {
  if (!ms) return null;
  return new Date(ms).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
}
// Draw time as m:ss, from milliseconds.
function fmtMS(ms) {
  if (!ms || ms < 0) return null;
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60), s = total % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}
// Aggregate duration as "5 hours" / "4h 20m" / "42 min", from milliseconds.
function fmtDur(ms) {
  const total = Math.round(ms / 1000);
  let m = Math.round(total / 60);
  const h = Math.floor(m / 60);
  m = m % 60;
  if (h > 0) return m === 0 ? `${h} hour${h > 1 ? "s" : ""}` : `${h}h ${m < 10 ? "0" : ""}${m}m`;
  return `${m} min`;
}

const PREFERS_REDUCED = typeof window !== "undefined"
  && window.matchMedia && window.matchMedia("(prefers-reduced-motion:reduce)").matches;

// Count-up number that animates once when scrolled into view.
function CountNum({ to, format }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    let raf;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      if (PREFERS_REDUCED) { setVal(to); return; }
      const t0 = performance.now(), dur = 1100;
      const tick = (now) => {
        const k = Math.min(1, (now - t0) / dur);
        setVal(Math.round(to * (1 - Math.pow(1 - k, 3))));
        if (k < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(ref.current);
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [to]);
  return <span ref={ref}>{format ? format(val) : val}</span>;
}

// Evergreen closing sign-off. Overridden per-event via shift.recapSignoff
// (edited in the recap share modal, carried into the recap doc by buildShiftRecap).
// First line renders as the headline; everything below it is smaller body copy.
const DEFAULT_SIGNOFF =
  "Every pancake here was made for you.\nThank you for having us. Share with your guests, and spread The Joy of Pancakes.";

// Split on the first line break → { head, body }. Operator controls the break.
function splitSignoff(raw) {
  const s = String(raw || "");
  const nl = s.indexOf("\n");
  return {
    head: (nl === -1 ? s : s.slice(0, nl)).trim(),
    body: nl === -1 ? "" : s.slice(nl + 1).trim(),
  };
}

function typeLabel(t) {
  if (!t) return "Custom";
  return t.charAt(0).toUpperCase() + t.slice(1);
}
function typeClass(t) {
  if (t === "freestyle") return "rx-free";
  if (t === "flash") return "rx-flash";
  return "";
}

export default function RecapPage({ shiftId }) {
  const [recap, setRecap] = useState(undefined); // undefined = loading, null = not found
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "Event recap · The Joy of Pancakes";
    return () => { document.title = "The Joy of Pancakes"; };
  }, []);

  useEffect(() => {
    if (!shiftId) { setRecap(null); return; }
    return onSnapshot(
      doc(db, "publicShiftRecaps", shiftId),
      (snap) => {
        if (!snap.exists()) { setRecap(null); return; }
        const data = snap.data();
        setRecap(data);
        if (data.eventName) {
          document.title = `${data.eventName} · The Joy of Pancakes`;
        }
      },
      () => setRecap(null),
    );
  }, [shiftId]);

  const pancakes = useMemo(() => recap?.pancakes || [], [recap]);

  useEffect(() => {
    if (lightboxIdx == null) return;
    function onKey(e) {
      if (e.key === "Escape") setLightboxIdx(null);
      else if (e.key === "ArrowRight") setLightboxIdx(i => Math.min(pancakes.length - 1, (i ?? 0) + 1));
      else if (e.key === "ArrowLeft")  setLightboxIdx(i => Math.max(0, (i ?? 0) - 1));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, pancakes]);

  function copyShareUrl() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => {});
  }

  // ── Derived stats (all from fields already on the recap doc) ──
  const stats = useMemo(() => {
    const withDraw = pancakes.filter(p => p.drawTimeMs > 0);
    const totalDrawMs = withDraw.reduce((s, p) => s + p.drawTimeMs, 0);
    const avgMs = withDraw.length ? totalDrawMs / withDraw.length : 0;
    const medCount = pancakes.filter(p => p.meditation).length;
    // Roster is strings on normal shifts, {name} objects on legacy content
    // sessions — handle both (strings used to fall through to per-pancake names).
    const rosterNames = (recap?.artistRoster || [])
      .map(a => (typeof a === "string" ? a : a?.name))
      .filter(Boolean);
    const distinctArtists = new Set(
      rosterNames.length ? rosterNames : pancakes.map(p => p.artistName).filter(Boolean)
    );

    // Superlatives — only meaningful once there are a few pancakes.
    let intricateId = null, fastestId = null, firstId = null, lastId = null;
    if (withDraw.length >= 3) {
      intricateId = withDraw.reduce((a, b) => (b.drawTimeMs > a.drawTimeMs ? b : a)).orderId;
      fastestId   = withDraw.reduce((a, b) => (b.drawTimeMs < a.drawTimeMs ? b : a)).orderId;
      if (fastestId === intricateId) fastestId = null; // all-equal edge case
    }
    if (pancakes.length >= 4) {
      const withTime = pancakes.filter(p => p.completedAtMs);
      if (withTime.length >= 2) {
        firstId = withTime.reduce((a, b) => (b.completedAtMs < a.completedAtMs ? b : a)).orderId;
        lastId  = withTime.reduce((a, b) => (b.completedAtMs > a.completedAtMs ? b : a)).orderId;
        if (firstId === lastId) { firstId = null; lastId = null; }
      }
    }
    // Booked window (open → close) — the hero headline. Framing the whole live
    // experience (not the summed drawing time) means the number always meets or
    // exceeds the hours the client paid for, so a recap can never read as a
    // "you only drew for 3 of my 4 hours" shortfall. See git history 2026-07-06.
    const windowMs = (recap?.openedAtMs && recap?.closedAtMs && recap.closedAtMs > recap.openedAtMs)
      ? recap.closedAtMs - recap.openedAtMs : 0;
    return {
      totalDrawMs, avgMs, medCount, windowMs,
      artistCount: distinctArtists.size,
      rosterNames,
      intricateId, fastestId, firstId, lastId,
    };
  }, [pancakes, recap]);

  const lightbox = lightboxIdx != null ? pancakes[lightboxIdx] : null;

  function ribbonFor(p) {
    if (p.orderId === stats.intricateId) return { cls: "", txt: "🏆 Most intricate" };
    if (p.orderId === stats.fastestId)   return { cls: "rx-fast", txt: "⚡ Fastest draw" };
    if (p.orderId === stats.firstId)     return { cls: "rx-first", txt: "🌅 First of the day" };
    if (p.orderId === stats.lastId)      return { cls: "rx-last", txt: "🌙 Last call" };
    return null;
  }

  return (
    <div className="recapx">
      <style>{RECAP_CSS}</style>
      <SiteNav />

      <main className="rx-wrap">
        {recap === undefined && (
          <div className="rx-loading">Loading…</div>
        )}

        {recap === null && (
          <div className="rx-notready">
            <div className="rx-notready-h">Recap not ready yet</div>
            <p>
              This recap hasn't been generated yet. If you're expecting a link from us, it'll go live within an hour of the event wrapping.{" "}
              <a href="/events">See upcoming events →</a>
            </p>
          </div>
        )}

        {recap && (
          <>
            {recap._isTestData && (
              <div className="rx-testbadge">
                🧪 TEST DATA — this recap came from a Screen Lab test shift, not a real event.
              </div>
            )}

            {/* ── Header ── */}
            <header className="rx-hero">
              <FairyDust />
              <div className="rx-hero-inner">
                <div className="rx-eyebrow">What we made together</div>
                <h1 className="rx-title">{recap.eventName || "Joy of Pancakes"}</h1>

                <div className="rx-meta">
                  {recap.openedAtMs && <span>{fmtDate(recap.openedAtMs)}</span>}
                  {recap.openedAtMs && recap.closedAtMs && <span className="rx-dot">•</span>}
                  {recap.openedAtMs && recap.closedAtMs && (
                    <span>{fmtTime(recap.openedAtMs)} – {fmtTime(recap.closedAtMs)}</span>
                  )}
                  {(recap.venueName || recap.venueAddress) && <span className="rx-dot">•</span>}
                  {(recap.venueName || recap.venueAddress) && (
                    <span>{recap.venueName || recap.venueAddress}</span>
                  )}
                </div>

                {stats.rosterNames.length > 0 && (
                  <div className="rx-roster">
                    <span className="rx-rk">Live art by</span> {stats.rosterNames.join(" · ")}
                  </div>
                )}

                <button className="rx-sharebtn" onClick={copyShareUrl}>
                  {copied ? "✓ Link copied" : "🔗 Share this recap"}
                </button>
              </div>
            </header>

            {/* ── Stats band ── */}
            {pancakes.length > 0 && (
              <section className="rx-stats" aria-label="Event by the numbers">
                {stats.windowMs > 0 ? (
                  <div className="rx-stat rx-hero-stat">
                    <div className="rx-big"><CountNum to={Math.round(stats.windowMs / 60000)} format={m => fmtDur(m * 60000)} /></div>
                    <div className="rx-cap">of live pancake art<small>start to finish, just for your guests</small></div>
                    <div className="rx-flare">🎨</div>
                  </div>
                ) : (
                  <div className="rx-stat rx-hero-stat">
                    <div className="rx-big"><CountNum to={recap.pancakeCount} /></div>
                    <div className="rx-cap">pancakes made<small>each one drawn live, just for the moment</small></div>
                    <div className="rx-flare">🥞</div>
                  </div>
                )}

                <StatCard ic="🥞" to={recap.pancakeCount} lab={recap.pancakeCount === 1 ? "pancake" : "pancakes"} />
                {stats.artistCount > 0 && (
                  <StatCard ic="🖌️" to={stats.artistCount} lab={stats.artistCount === 1 ? "artist" : "artists"} />
                )}
                {stats.medCount > 0 && (
                  <StatCard ic="✦" to={stats.medCount} lab={stats.medCount === 1 ? "meditation" : "meditations"} />
                )}
                {stats.avgMs > 0 && (
                  <StatCard ic="⏱️" to={Math.round(stats.avgMs / 1000)} lab="of live art each" format={s => fmtMS(s * 1000)} />
                )}
              </section>
            )}

            {/* ── Grid ── */}
            {pancakes.length === 0 ? (
              <div className="rx-empty">No finished pancakes captured for this event yet.</div>
            ) : (
              <>
                <div className="rx-sect">
                  <h2>Every pancake from your day</h2>
                  <span className="rx-subh">in the order they came off the griddle</span>
                </div>
                <div className="rx-grid">
                  {pancakes.map((p, i) => {
                    const ribbon = ribbonFor(p);
                    const draw = fmtMS(p.drawTimeMs);
                    return (
                      <button key={p.orderId} className="rx-card" onClick={() => setLightboxIdx(i)}>
                        {ribbon && <span className={`rx-ribbon ${ribbon.cls}`}>{ribbon.txt}</span>}
                        {p.meditation && <span className="rx-medmark" title="Has a meditation">✦</span>}
                        <div className="rx-pan">
                          <img
                            src={p.thumbUrl || p.cutoutUrl || p.photoUrl}
                            alt={p.aiDescription || p.title || "Pancake"}
                            loading="lazy"
                          />
                        </div>
                        <div className="rx-cmeta">
                          <div className="rx-cname">{p.title}</div>
                          <div className="rx-csub">
                            <span className={`rx-seqpill ${typeClass(p.orderType)}`}>
                              {typeLabel(p.orderType)}{p.designSequence != null ? ` #${p.designSequence}` : ""}
                            </span>
                            {draw && <span className="rx-draw">{draw}</span>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* ── Closing sign-off (operator-customizable, evergreen default) ── */}
            {pancakes.length > 0 && (
              <section className="rx-closing">
                {(() => {
                  const { head, body } = splitSignoff(recap.recapSignoff || DEFAULT_SIGNOFF);
                  return (
                    <>
                      {head && <p className="rx-signoff">{head}</p>}
                      {body && <p className="rx-signoff-sub">{body}</p>}
                    </>
                  );
                })()}
                <div className="rx-cta-row">
                  <button className="rx-btn rx-gold" onClick={copyShareUrl}>
                    {copied ? "✓ Link copied" : "🔗 Share this recap"}
                  </button>
                  <a className="rx-btn rx-ghost" href="/quote">Book The Joy of Pancakes →</a>
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* ── Lightbox ── */}
      {lightbox && (
        <div className="rx-lb" role="dialog" aria-modal="true" aria-label="Pancake detail"
          onClick={() => setLightboxIdx(null)}>
          <div className="rx-lb-card" onClick={e => e.stopPropagation()}>
            <button className="rx-lb-close" onClick={() => setLightboxIdx(null)} aria-label="Close">✕</button>

            <div className="rx-lb-pan">
              <img
                src={lightbox.cutoutUrl || lightbox.photoUrl || lightbox.thumbUrl}
                alt={lightbox.aiDescription || lightbox.title || "Pancake"}
              />
            </div>

            <div className="rx-lb-title">{lightbox.title}</div>
            <div className="rx-lb-by">
              {lightbox.artistName ? `by ${lightbox.artistName}` : ""}
              {lightbox.artistName && lightbox.completedAtMs ? "  ·  " : ""}
              {lightbox.completedAtMs ? `made ${fmtTime(lightbox.completedAtMs)}` : ""}
            </div>

            <div className="rx-chips">
              <Chip k="Type" v={`${typeLabel(lightbox.orderType)}${lightbox.designSequence != null ? ` #${lightbox.designSequence}` : ""}`} />
              {fmtMS(lightbox.drawTimeMs) && <Chip k="Draw time" v={fmtMS(lightbox.drawTimeMs)} gold />}
              {lightbox.artistName && <Chip k="Artist" v={lightbox.artistName} />}
              {lightbox.completedAtMs && <Chip k="Finished" v={fmtTime(lightbox.completedAtMs)} />}
            </div>

            {(lightbox.tags || []).length > 0 && (
              <div className="rx-tags">
                {lightbox.tags.map((t, i) => <span key={i} className="rx-tag">{t}</span>)}
              </div>
            )}

            {lightbox.meditation && (
              <div className="rx-med">
                <div className="rx-med-h">✦ A meditation for this one</div>
                <p>{lightbox.meditation}</p>
              </div>
            )}

            {!lightbox.meditation && lightbox.aiDescription && (
              <p className="rx-lb-desc">{lightbox.aiDescription}</p>
            )}

            <div className="rx-lb-nav">
              <button className="rx-navbtn" onClick={() => setLightboxIdx(i => Math.max(0, (i ?? 0) - 1))} disabled={lightboxIdx === 0}>← Previous</button>
              <span className="rx-lb-count">{lightboxIdx + 1} / {pancakes.length}</span>
              <button className="rx-navbtn" onClick={() => setLightboxIdx(i => Math.min(pancakes.length - 1, (i ?? 0) + 1))} disabled={lightboxIdx === pancakes.length - 1}>Next →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ ic, to, lab, format }) {
  return (
    <div className="rx-stat">
      <div className="rx-ic">{ic}</div>
      <div className="rx-num"><CountNum to={to} format={format} /></div>
      <div className="rx-lab">{lab}</div>
    </div>
  );
}

function Chip({ k, v, gold }) {
  return (
    <div className="rx-chip">
      <div className="rx-chip-k">{k}</div>
      <div className={`rx-chip-v${gold ? " g" : ""}`}>{v}</div>
    </div>
  );
}

function FairyDust() {
  const sparks = useMemo(() => {
    const n = PREFERS_REDUCED ? 10 : 26;
    return Array.from({ length: n }, () => ({
      left: (Math.random() * 100).toFixed(2),
      top: (Math.random() * 100).toFixed(2),
      d: (3 + Math.random() * 4).toFixed(2),
      dl: (Math.random() * 5).toFixed(2),
      sc: (0.5 + Math.random()).toFixed(2),
    }));
  }, []);
  return (
    <div className="rx-dust" aria-hidden="true">
      {sparks.map((s, i) => (
        <span key={i} className="rx-spark" style={{
          left: `${s.left}%`, top: `${s.top}%`,
          "--d": `${s.d}s`, "--dl": `${s.dl}s`, transform: `scale(${s.sc})`,
        }} />
      ))}
    </div>
  );
}

// Scoped styles — everything under .recapx. Uses global --jop-* tokens.
const RECAP_CSS = `
.recapx{ position:relative; min-height:100vh; overflow-x:hidden;
  color:var(--jop-cream); font-family:var(--jop-display);
  -webkit-font-smoothing:antialiased;
  background:
    radial-gradient(70% 40% at 50% -6%, rgba(var(--jop-orange-rgb),0.20), transparent 58%),
    radial-gradient(80% 55% at 50% 33%, rgba(var(--jop-blue-rgb),0.24), transparent 70%),
    linear-gradient(180deg, #1a1f4a 0%, #131b46 24%, #0e1638 56%, #0a1028 100%);
  background-attachment:fixed, fixed, fixed; }
.recapx::after{ content:""; position:fixed; inset:0; z-index:0; pointer-events:none;
  background:radial-gradient(60% 30% at 50% 106%, rgba(var(--jop-pink-rgb),0.10), transparent 70%); }
.recapx > *{ position:relative; z-index:1; }
.rx-wrap{ max-width:1120px; margin:0 auto; padding:0 1.25rem 5rem; }
.rx-loading{ text-align:center; color:rgba(var(--jop-cream-rgb),0.5); padding:5rem 1rem; }
.rx-notready{ text-align:center; padding:3rem; margin-top:5.5rem;
  border:1px dashed rgba(var(--jop-gold-rgb),0.35); border-radius:14px; color:rgba(var(--jop-cream-rgb),0.7); }
.rx-notready-h{ font-family:var(--jop-script); font-size:1.9rem; color:var(--jop-gold); margin-bottom:.5rem; }
.rx-notready p{ font-size:.92rem; line-height:1.55; max-width:420px; margin:0 auto; }
.rx-notready a{ color:var(--jop-pink); font-weight:700; text-decoration:none; }
.rx-testbadge{ background:rgba(var(--jop-gold-rgb),0.14); border:1px solid rgba(var(--jop-gold-rgb),0.55);
  color:var(--jop-gold); border-radius:12px; padding:.6rem 1rem; margin:5.5rem 0 1.5rem;
  font-weight:700; font-size:.85rem; text-align:center; }

/* Header */
.rx-hero{ position:relative; padding:5.5rem 0 1.25rem; text-align:center; }
.rx-testbadge + .rx-hero{ padding-top:1rem; }
.rx-dust{ position:absolute; inset:-1rem 0 auto; height:340px; pointer-events:none; z-index:0; }
.rx-spark{ position:absolute; width:6px; height:6px; border-radius:50%;
  background:radial-gradient(circle, rgba(255,240,200,0.95), rgba(var(--jop-gold-rgb),0.4) 55%, transparent 70%);
  opacity:0; animation:rx-tw var(--d,5s) ease-in-out var(--dl,0s) infinite; }
@keyframes rx-tw{ 0%,100%{opacity:0; transform:translateY(6px) scale(.6);} 50%{opacity:.9; transform:translateY(-6px) scale(1);} }
.rx-hero-inner{ position:relative; z-index:1; }
.rx-eyebrow{ font-size:.82rem; letter-spacing:.28em; text-transform:uppercase;
  color:rgba(var(--jop-gold-rgb),0.85); font-weight:800; margin-bottom:.9rem; }
.rx-title{ margin:0; font-family:var(--jop-script); font-weight:700; letter-spacing:-.01em;
  font-size:clamp(2.6rem,7vw,4.4rem); line-height:1.05;
  color:#fff; filter:drop-shadow(0 6px 30px rgba(var(--jop-gold-rgb),0.16)); text-wrap:balance; }
.rx-meta{ margin-top:1rem; display:flex; flex-wrap:wrap; gap:.35rem .9rem; justify-content:center;
  color:rgba(var(--jop-cream-rgb),0.78); font-size:1rem; font-weight:600; }
.rx-dot{ color:rgba(var(--jop-cream-rgb),0.35); }
.rx-roster{ margin-top:.8rem; color:var(--jop-highlight-blue); font-weight:800; font-size:.95rem; }
.rx-rk{ font-size:.72rem; letter-spacing:.16em; text-transform:uppercase; color:rgba(var(--jop-cream-rgb),0.45); font-weight:800; }
.rx-sharebtn{ margin-top:1.25rem; font-family:inherit; cursor:pointer; font-weight:800; font-size:.9rem;
  border-radius:999px; padding:.6rem 1.25rem; color:var(--jop-pink);
  background:rgba(var(--jop-pink-rgb),0.12); border:1px solid rgba(var(--jop-pink-rgb),0.4);
  transition:transform .12s ease; }
.rx-sharebtn:hover{ transform:translateY(-2px); }

/* Stats */
.rx-stats{ display:grid; gap:.85rem; margin:2.2rem 0 3rem; grid-template-columns:repeat(4,1fr); }
.rx-stat{ position:relative; border-radius:20px; padding:1.2rem 1.15rem;
  background:linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.02));
  border:1px solid rgba(var(--jop-cream-rgb),0.10);
  box-shadow:0 10px 30px rgba(0,0,0,0.22) inset, 0 1px 0 rgba(255,255,255,0.04); }
.rx-ic{ font-size:1.15rem; }
.rx-num{ font-family:var(--jop-display); font-weight:800; line-height:1;
  font-variant-numeric:tabular-nums; font-size:clamp(1.9rem,4.2vw,2.7rem); color:#fff; }
.rx-lab{ font-size:.72rem; letter-spacing:.13em; text-transform:uppercase;
  color:rgba(var(--jop-cream-rgb),0.55); font-weight:800; margin-top:.35rem; }
.rx-hero-stat{ grid-column:span 4; display:flex; align-items:center; gap:1.1rem; flex-wrap:wrap;
  background:
    radial-gradient(120% 160% at 12% 0%, rgba(var(--jop-gold-rgb),0.20), transparent 55%),
    linear-gradient(180deg, rgba(var(--jop-orange-rgb),0.10), rgba(255,255,255,0.02));
  border-color:rgba(var(--jop-gold-rgb),0.42);
  box-shadow:0 16px 44px rgba(var(--jop-orange-rgb),0.12); }
.rx-big{ font-family:var(--jop-display); font-weight:800; line-height:.95;
  font-variant-numeric:tabular-nums; font-size:clamp(2.8rem,9vw,4.6rem);
  color:var(--jop-gold); }
.rx-cap{ font-size:1.05rem; font-weight:700; color:var(--jop-cream); }
.rx-cap small{ display:block; color:rgba(var(--jop-cream-rgb),0.6); font-weight:600; font-size:.85rem; margin-top:.15rem; }
.rx-flare{ margin-left:auto; font-size:2.4rem; filter:drop-shadow(0 4px 12px rgba(var(--jop-gold-rgb),0.35)); }

/* Section label */
.rx-sect{ display:flex; align-items:baseline; gap:.7rem; margin:0 0 1.15rem; flex-wrap:wrap; }
.rx-sect h2{ margin:0; font-family:var(--jop-display); font-weight:800; font-size:1.35rem; color:#fff; }
.rx-subh{ color:rgba(var(--jop-cream-rgb),0.5); font-size:.9rem; font-weight:600; }
.rx-empty{ text-align:center; padding:2rem; border:1px dashed rgba(var(--jop-cream-rgb),0.15);
  border-radius:12px; color:rgba(var(--jop-cream-rgb),0.55); font-size:.92rem; }

/* Grid */
.rx-grid{ display:grid; gap:1.05rem; grid-template-columns:repeat(auto-fill,minmax(184px,1fr)); }
.rx-card{ position:relative; cursor:pointer; text-align:left; color:inherit; font-family:inherit; padding:.85rem;
  background:linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.015));
  border:1px solid rgba(var(--jop-cream-rgb),0.10); border-radius:18px;
  transition:transform .18s ease, border-color .18s ease, box-shadow .18s ease; }
.rx-card:hover,.rx-card:focus-visible{ transform:translateY(-4px); border-color:rgba(var(--jop-gold-rgb),0.5);
  box-shadow:0 18px 40px rgba(0,0,0,0.32); outline:none; }
.rx-card:hover .rx-pan img,.rx-card:focus-visible .rx-pan img{ transform:rotate(-3deg) scale(1.04); }
.rx-pan{ position:relative; aspect-ratio:1/1; display:flex; align-items:center; justify-content:center; }
.rx-pan img{ max-width:100%; max-height:100%; object-fit:contain; transition:transform .25s ease;
  filter:drop-shadow(0 10px 20px rgba(0,0,0,0.45)); }
.rx-ribbon{ position:absolute; top:.55rem; left:.55rem; z-index:2; font-size:.66rem; font-weight:800;
  padding:.22rem .5rem; border-radius:999px; display:inline-flex; align-items:center; gap:.25rem;
  background:linear-gradient(180deg,#fff2cf,var(--jop-gold)); color:#5b2f10;
  box-shadow:0 4px 12px rgba(var(--jop-gold-rgb),0.4); }
.rx-ribbon.rx-fast{ background:linear-gradient(180deg,#ffd0e6,var(--jop-pink)); color:#5a1030; }
.rx-ribbon.rx-first{ background:linear-gradient(180deg,#ffd9a1,var(--jop-orange)); color:#fff; }
.rx-ribbon.rx-last{ background:linear-gradient(180deg,#cfe3ff,var(--jop-highlight-blue)); color:#0b234a; }
.rx-medmark{ position:absolute; top:.5rem; right:.5rem; z-index:2; width:26px; height:26px; border-radius:50%;
  display:grid; place-items:center; background:rgba(var(--jop-gold-rgb),0.9); color:#5b2f10; font-size:.8rem;
  box-shadow:0 3px 10px rgba(0,0,0,0.3); }
.rx-cmeta{ margin-top:.55rem; }
.rx-cname{ font-weight:800; font-size:1rem; color:#fff; line-height:1.15; }
.rx-csub{ margin-top:.18rem; display:flex; gap:.5rem; align-items:center; }
.rx-draw{ color:var(--jop-gold); font-variant-numeric:tabular-nums; font-weight:800; font-size:.78rem; }
.rx-seqpill{ display:inline-block; font-size:.64rem; font-weight:800; letter-spacing:.03em;
  padding:.12rem .42rem; border-radius:6px; text-transform:uppercase;
  background:rgba(var(--jop-highlight-blue-rgb),0.16); color:var(--jop-highlight-blue); }
.rx-seqpill.rx-free{ background:rgba(var(--jop-pink-rgb),0.16); color:var(--jop-pink); }
.rx-seqpill.rx-flash{ background:rgba(var(--jop-gold-rgb),0.16); color:var(--jop-gold); }

/* Closing */
.rx-closing{ margin-top:3.2rem; border-radius:24px; padding:2.4rem 1.6rem; text-align:center; overflow:hidden;
  background:
    radial-gradient(90% 120% at 50% 0%, rgba(var(--jop-pink-rgb),0.13), transparent 60%),
    linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.015));
  border:1px solid rgba(var(--jop-cream-rgb),0.12); }
.rx-signoff{ margin:0 auto .5rem; max-width:24ch; font-family:var(--jop-display); font-weight:800;
  font-size:clamp(1.5rem,4.2vw,2.2rem); line-height:1.2; color:#fff; text-wrap:balance; }
.rx-signoff-sub{ margin:0 auto 1.5rem; max-width:46ch; line-height:1.55; white-space:pre-line;
  color:rgba(var(--jop-cream-rgb),0.8); font-size:1rem; text-wrap:balance; }
.rx-cta-row{ display:flex; gap:.7rem; justify-content:center; flex-wrap:wrap; }
.rx-btn{ font-family:inherit; font-weight:800; font-size:.95rem; cursor:pointer; text-decoration:none;
  border-radius:999px; padding:.75rem 1.4rem; border:1px solid transparent;
  display:inline-flex; align-items:center; gap:.45rem; transition:transform .12s ease; }
.rx-btn:hover{ transform:translateY(-2px); }
.rx-gold{ background:linear-gradient(180deg,#ffd873,var(--jop-gold)); color:#5b2f10;
  box-shadow:0 10px 26px rgba(var(--jop-gold-rgb),0.32); }
.rx-ghost{ background:rgba(255,255,255,0.04); color:var(--jop-cream); border-color:rgba(var(--jop-cream-rgb),0.2); }

/* Lightbox */
.rx-lb{ position:fixed; inset:0; z-index:200; display:flex; padding:1rem; align-items:center; justify-content:center;
  background:rgba(6,10,22,0.88); backdrop-filter:blur(12px); }
.rx-lb-card{ width:100%; max-width:560px; max-height:94vh; overflow-y:auto; border-radius:24px; padding:1.5rem; position:relative;
  background:linear-gradient(180deg, #171c38, #0f1430);
  border:1px solid rgba(var(--jop-gold-rgb),0.3); box-shadow:0 30px 80px rgba(0,0,0,0.6);
  animation:rx-pop .28s cubic-bezier(.2,.9,.3,1.2); }
@keyframes rx-pop{ from{opacity:0; transform:translateY(14px) scale(.96);} to{opacity:1; transform:none;} }
.rx-lb-close{ position:absolute; top:.8rem; right:.8rem; width:36px; height:36px; border-radius:50%;
  background:rgba(0,0,0,0.4); border:1px solid rgba(var(--jop-cream-rgb),0.2); color:var(--jop-cream);
  font-size:1.05rem; cursor:pointer; display:grid; place-items:center; font-family:inherit; }
.rx-lb-pan{ aspect-ratio:1/1; max-width:300px; margin:0 auto .3rem; display:flex; align-items:center; justify-content:center; }
.rx-lb-pan img{ max-width:100%; max-height:100%; object-fit:contain; filter:drop-shadow(0 18px 34px rgba(0,0,0,0.5)); }
.rx-lb-title{ text-align:center; margin:.2rem 0 .1rem; font-family:var(--jop-script); font-weight:700;
  font-size:1.85rem; color:var(--jop-gold); line-height:1.1; }
.rx-lb-by{ text-align:center; color:rgba(var(--jop-cream-rgb),0.6); font-size:.9rem; font-weight:600; margin-bottom:1rem; }
.rx-chips{ display:grid; grid-template-columns:repeat(2,1fr); gap:.55rem; margin-bottom:1rem; }
.rx-chip{ border-radius:12px; padding:.6rem .75rem; background:rgba(255,255,255,0.04); border:1px solid rgba(var(--jop-cream-rgb),0.1); }
.rx-chip-k{ font-size:.66rem; letter-spacing:.1em; text-transform:uppercase; color:rgba(var(--jop-cream-rgb),0.5); font-weight:800; }
.rx-chip-v{ font-size:1.05rem; font-weight:800; color:#fff; font-variant-numeric:tabular-nums; margin-top:.15rem; }
.rx-chip-v.g{ color:var(--jop-gold); }
.rx-tags{ display:flex; flex-wrap:wrap; gap:.4rem; margin-bottom:1rem; justify-content:center; }
.rx-tag{ font-size:.74rem; font-weight:700; padding:.28rem .6rem; border-radius:999px;
  background:rgba(var(--jop-highlight-blue-rgb),0.14); color:var(--jop-highlight-blue); }
.rx-med{ border-radius:14px; padding:1rem 1.15rem; margin-bottom:1rem;
  background:radial-gradient(120% 140% at 0% 0%, rgba(var(--jop-gold-rgb),0.14), transparent 60%), rgba(var(--jop-gold-rgb),0.05);
  border:1px solid rgba(var(--jop-gold-rgb),0.3); }
.rx-med-h{ font-size:.7rem; letter-spacing:.12em; text-transform:uppercase; color:var(--jop-gold); font-weight:800; margin-bottom:.5rem; }
.rx-med p{ margin:0; font-size:1rem; line-height:1.66; color:rgba(var(--jop-cream-rgb),0.96); font-style:italic; white-space:pre-wrap; }
.rx-lb-desc{ margin:0 0 1rem; color:rgba(var(--jop-cream-rgb),0.75); font-size:.9rem; line-height:1.55; font-style:italic; text-align:center; }
.rx-lb-nav{ display:flex; justify-content:space-between; align-items:center; gap:.5rem; }
.rx-navbtn{ font-family:inherit; font-weight:800; font-size:.85rem; cursor:pointer; border-radius:10px; padding:.5rem .85rem;
  background:rgba(var(--jop-highlight-blue-rgb),0.14); color:var(--jop-highlight-blue); border:1px solid rgba(var(--jop-highlight-blue-rgb),0.35); }
.rx-navbtn:disabled{ opacity:.35; cursor:not-allowed; }
.rx-lb-count{ color:rgba(var(--jop-cream-rgb),0.5); font-size:.82rem; font-variant-numeric:tabular-nums; }

@media (max-width:640px){
  .rx-stats{ grid-template-columns:repeat(2,1fr); }
  .rx-hero-stat{ grid-column:span 2; }
}
@media (prefers-reduced-motion:reduce){
  .rx-spark{ animation:none; opacity:.5; }
  .rx-lb-card{ animation:none; }
  .rx-card{ transition:none; }
}
`;
